import React, { useState, useEffect } from 'react';
import axios from 'axios';
import FormattedDate from '../../../../utils/DateFormat';
import formatCurrency from '../../../../utils/formatCurrency';
import LoadingAnimation from '../../../../components/anims/LoadingAnimation';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';

// Formato de cantidades, limitando decimales
const formatQuantity = (value) => {
    return parseFloat(value).toFixed(2);
};

// Componente principal de React
const TransaccionesVentaProducto = ({ id_producto, token, es_padre }) => {
    // --- Estados del componente ---
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const limit = 10; 

    // --- Función para obtener los datos de la API ---
    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const offset = (currentPage - 1) * limit;
            const response = await axios.get(`/api/productos/ventas/${id_producto}?offset=${offset}&limit=${limit}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const { data, total } = response.data;
            setTotal(total);
            setData(data);
            setTotalPages(Math.ceil(total / limit));
        } catch (err) {
            console.error('Error al cargar las transacciones:', err);
            setError('Error al cargar los datos de las transacciones.');
        } finally {
            setLoading(false);
        }
    };

    // --- Efecto para llamar a la API cuando cambie la página o el ID del producto ---
    useEffect(() => {
        if (id_producto) {
            fetchData();
        }
    }, [id_producto, currentPage]);

    // --- Lógica de renderizado condicional ---
    if (loading) {
        return <LoadingAnimation />;
    }

    if (error) {
        return <div className="text-center text-red-500 font-semibold p-4">{error}</div>;
    }

    if (data.length === 0) {
        return (
            <div className="text-center text-gray-500 font-semibold text-lg p-4">
                No existen movimientos de venta registrados para este producto.
            </div>
        );
    }

    // --- Renderizado del componente ---
    return (
        <div className="p-4 mx-auto flex flex-col justify-between">
            <p className="mb-4 text-sm md:text-base">
                Total Ventas: <strong>{total}</strong>
            </p>
            <div className="space-y-4">
                {data.map((item, index) => {
                    const documentName = item.tipoDocumento === 1 ? 'Factura de Venta' : 'Documento Pre-Electrónico';
                    const documentNumber = item.tipoDocumento === 1 ? item.nFactura : item.n_transacion;
                    const totalFactura = formatCurrency(parseFloat(item.total_neto));

                    return (
                        <div
                            key={index}
                            className="p-4 bg-white shadow-md rounded-lg border border-teal-500 transition-all duration-300 hover:shadow-lg"
                        >
                            <div className="flex flex-wrap justify-between items-center mb-2 gap-2">
                                <span className="text-xs md:text-sm text-gray-500 font-medium">
                                    {FormattedDate(item.fecha_registro)}
                                </span>
                                <span className="font-semibold bg-yellow-500 px-3 border-yellow-500 rounded-full text-black-500 text-xs md:text-sm">
                                    {item.nombre_cliente}
                                    {item.nombre_cliente.toLowerCase().includes('consumidor') ? '' : ` - ${item.identificacion}`}
                                </span>
                                <span className="text-xs md:text-sm text-gray-700">
                                    <span className="font-semibold text-teal-600">
                                        {documentName}:
                                    </span>{' '}
                                    {documentNumber}
                                </span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-2 gap-x-4 text-sm md:text-base">
                                <p>
                                    <span className="font-semibold text-gray-800">Cantidad:</span>{' '}
                                    {formatQuantity(item.cantidad)}
                                </p>
                                <p>
                                    <span className="font-semibold text-gray-800">Costo:</span>{' '}
                                    {formatCurrency(item.costo)}
                                </p>
                                <p>
                                    <span className="font-semibold text-gray-800">Total:</span>{' '}
                                    {formatCurrency(item.total)}
                                </p>
                                <p>
                                    <span className="font-semibold text-gray-800">Utilidad: </span>
                                    <span
                                        className={`font-bold border rounded-full px-2 py-1 text-white ${
                                            parseFloat(item.porcentaje_utilidad) >= 50
                                                ? 'bg-green-500 border-green-500'
                                                : parseFloat(item.porcentaje_utilidad) <= 30
                                                ? 'bg-red-500 border-red-500'
                                                : parseFloat(item.porcentaje_utilidad) >= 31 &&
                                                  parseFloat(item.porcentaje_utilidad) <= 39
                                                ? 'bg-yellow-600 border-yellow-600'
                                                : 'bg-blue-500 border-blue-500'
                                        }`}
                                    >
                                        {`${parseFloat(item.porcentaje_utilidad).toFixed(2)}%`}
                                    </span>
                                </p>
                                <p>
                                    <span className="font-semibold text-gray-800">Total Factura:</span>{' '}
                                    {totalFactura}
                                </p>
                                {es_padre && (
                                    <p>
                                        <span className="font-semibold text-gray-800">Producto Variante: </span>{' '}
                                        <strong>
                                            <a
                                                href={`/productos/vista/${id_producto}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-teal-600 underline"
                                            >
                                                {id_producto}
                                            </a>
                                        </strong>
                                    </p>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Controles de paginación */}
            {totalPages > 1 && (
                <div className="mt-6 flex justify-center items-center space-x-2">
                    <button
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="px-2 py-2 bg-yellow-500 text-black-500 border-yellow-500 rounded-full disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base"
                    >
                        <FaArrowLeft />
                    </button>
                    <span className="text-sm md:text-base font-semibold text-gray-700">
                        Página {currentPage} de {totalPages}
                    </span>
                    <button
                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="px-2 py-2 bg-yellow-500 text-black-500 border-yellow-500 rounded-full disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base"
                    >
                        <FaArrowRight />
                    </button>
                </div>
            )}
        </div>
    );
};

export default TransaccionesVentaProducto;
